/**
 * Onboarding. iOS storage is per-container (Safari tabs ≠ Home Screen app), so iPhone
 * users are told to install to the Home Screen first and use the app from there (SPEC §6.1).
 */
import { useEffect, useState } from 'react'
import { Logo } from '../components/Logo'
import { isAndroid, isIOS, isStandalone } from '../lib/haptics'
import { subscribePush } from '../lib/push'
import { useMe } from '../store/hooks'

type BIP = Event & { prompt: () => Promise<void>; userChoice: Promise<{ outcome: string }> }
let deferred: BIP | null = null
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault()
    deferred = e as BIP
  })
}

export function markOnboarded() {
  localStorage.setItem('tz.onboarded', '1')
}

export function needsOnboarding(): boolean {
  return localStorage.getItem('tz.onboarded') !== '1'
}

export default function Welcome({ onDone }: { onDone: () => void }) {
  const me = useMe()
  const [step] = useState<'install' | 'notify'>(isIOS && !isStandalone ? 'install' : 'notify')
  const [canInstall, setCanInstall] = useState(!!deferred)
  useEffect(() => {
    const f = () => setCanInstall(true)
    window.addEventListener('beforeinstallprompt', f)
    return () => window.removeEventListener('beforeinstallprompt', f)
  }, [])
  const finish = () => {
    markOnboarded()
    onDone()
  }
  return (
    <div className="flex min-h-dvh flex-col items-center justify-center px-6 text-center" style={{ paddingTop: 'var(--safe-t)' }}>
      <div className="w-full max-w-sm">
        <Logo size={56} className="mx-auto" />
        <h1 className="pt-5 text-2xl font-semibold tracking-tight">Hi {me.user?.name.split(' ')[0] ?? 'there'}</h1>
        {step === 'install' ? (
          <>
            <p className="pt-2 text-[0.9375rem] text-muted">
              On iPhone, install Terrazzo to your Home Screen first and use it from there — that copy keeps your offline data and can send notifications.
            </p>
            <ol className="mx-auto mt-6 space-y-3 text-left text-[0.9375rem]">
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-sm bg-surface-3 text-xs font-semibold">1</span>
                <span>
                  Tap the <strong>Share</strong> button in Safari’s toolbar.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-sm bg-surface-3 text-xs font-semibold">2</span>
                <span>
                  Choose <strong>Add to Home Screen</strong>, then <strong>Add</strong>.
                </span>
              </li>
              <li className="flex gap-3">
                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-sm bg-surface-3 text-xs font-semibold">3</span>
                <span>Open Terrazzo from your Home Screen and sign in once more.</span>
              </li>
            </ol>
            <p className="pt-6 text-xs text-muted">Links opened from other apps (like Claude) open in Safari — that works too, it just keeps its own copy.</p>
            <button type="button" onClick={finish} className="mt-6 h-11 w-full rounded-xl border border-line-2 text-sm">
              Continue in Safari for now
            </button>
          </>
        ) : (
          <>
            <p className="pt-2 text-[0.9375rem] text-muted">Get reminders, a morning summary, and a heads-up when your partner adds things. You can change this any time in Settings.</p>
            <button
              type="button"
              onClick={() => {
                void subscribePush(me.vapidPublicKey)
                  .catch(() => {})
                  .finally(finish)
              }}
              disabled={!me.vapidPublicKey || !('Notification' in window)}
              className="mt-8 h-12 w-full rounded-xl bg-fg font-medium text-bg disabled:opacity-40"
            >
              Turn on notifications
            </button>
            {isAndroid && canInstall && !isStandalone && (
              <button
                type="button"
                onClick={() => {
                  void deferred?.prompt()
                  deferred = null
                  setCanInstall(false)
                }}
                className="mt-3 h-11 w-full rounded-xl border border-line-2 text-sm"
              >
                Install the app
              </button>
            )}
            <button type="button" onClick={finish} className="mt-3 h-11 w-full rounded-xl text-sm text-muted">
              Not now
            </button>
          </>
        )}
      </div>
    </div>
  )
}
